import { useState } from "react";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n/LangContext";
import { LeadOtpVerification, useLeadOtp } from "@/components/LeadOtpVerification";
import UaeWhatsAppPhoneInput from "@/components/UaeWhatsAppPhoneInput";

const createInitial = (direction = "gold") => ({
  name: "",
  company: "",
  email: "",
  messenger_type: "whatsapp",
  messenger: "",
  direction,
  volume: "",
  message: "",
});

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]/60 focus:bg-white/[0.06]";

export const ContactForm = ({ defaultDirection = "gold", variant = "market" }) => {
  const [form, setForm] = useState(() => createInitial(defaultDirection));
  const { t } = useLang();
  const isB2b = variant === "b2b";
  const otp = useLeadOtp({ form, resetForm: () => setForm(createInitial(defaultDirection)), t });

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = (event) => {
    event.preventDefault();
    otp.submit();
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#D4AF37]/80">{t("contact.overline")}</p>
          <h2 className="mt-6 font-serif text-3xl font-semibold leading-snug tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            {isB2b ? t("contact.generalTitle") : t("contact.title")}
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{isB2b ? t("contact.generalText") : t("contact.text")}</p>
          <p className="mt-10 max-w-md text-xs leading-relaxed text-slate-600">{t("contact.legal")}</p>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="lead-form" className="rounded-[2.5rem] border border-white/10 bg-[#12141C]/90 p-6 backdrop-blur-xl sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <input required value={form.name} onChange={set("name")} placeholder={t("contact.namePh")} data-testid="lead-name-input" className={inputCls} />
              <input required value={form.company} onChange={set("company")} placeholder={t("contact.companyPh")} data-testid="lead-company-input" className={inputCls} />
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <input required type="email" value={form.email} onChange={set("email")} placeholder={t("contact.emailPh")} data-testid="lead-email-input" className={inputCls} />
              <UaeWhatsAppPhoneInput required value={form.messenger} onChange={(messenger) => setForm((current) => ({ ...current, messenger }))} placeholder={t("contact.whatsappPh")} testId="lead-messenger-input" className={inputCls} />
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">{t("contact.whatsappOnly")}</p>

            <div className="mt-5" data-testid="lead-direction-selector">
              <p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-slate-500">{t("contact.directionLabel")}</p>
              <div className="grid grid-cols-2 gap-2">
                {t("contact.directions").map((direction) => (
                  <button
                    key={direction.value}
                    type="button"
                    data-testid={`lead-direction-${direction.value}`}
                    onClick={() => setForm((current) => ({ ...current, direction: direction.value }))}
                    className={`min-h-12 min-w-0 rounded-xl border px-2 py-3 text-xs font-medium leading-tight transition-all duration-300 sm:px-3 sm:text-sm ${
                      form.direction === direction.value
                        ? "border-[#D4AF37]/60 bg-[#D4AF37]/12 text-[#F3E5AB]"
                        : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25"
                    }`}
                  >
                    {direction.label}
                  </button>
                ))}
              </div>
            </div>

            <input value={form.volume} onChange={set("volume")} placeholder={isB2b ? t("contact.generalScopePh") : t("contact.volumePh")} data-testid="lead-volume-input" className={`${inputCls} mt-5`} />
            <textarea value={form.message} onChange={set("message")} placeholder={t("contact.messagePh")} rows={4} data-testid="lead-message-input" className={`${inputCls} mt-5 resize-none`} />
            <LeadOtpVerification otp={otp} t={t} variant="dark" directSubmitLabel={t("contact.submit")} />
          </form>
        </Reveal>
      </div>
    </section>
  );
};
